import type { WIPReport, WipAgeBand, WipAgingBands } from '@/types';

export const WIP_AGE_BANDS: WipAgeBand[] = ['0-30', '31-60', '61-90', '91-120', '121+'];

export function emptyWipAgingBands(): WipAgingBands {
  return { '0-30': 0, '31-60': 0, '61-90': 0, '91-120': 0, '121+': 0 };
}

export function bandForAgeDays(days: number): WipAgeBand {
  if (days <= 30) return '0-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  if (days <= 120) return '91-120';
  return '121+';
}

/**
 * Band split for a project's unbilled WIP. Live rows carry the split calculated from the
 * dates of the individual unbilled ledger entries; demo/tour rows only know a single age,
 * so their whole WIP amount sits in the band implied by that age.
 */
export function wipAgingBandsFor(report: WIPReport): WipAgingBands {
  if (report.agingBands) return report.agingBands;
  const bands = emptyWipAgingBands();
  bands[bandForAgeDays(report.agingDays ?? 0)] = report.wipAmount;
  return bands;
}

export interface WipAgingBandApiFields {
  wipAging0To30?: number | null;
  wipAging31To60?: number | null;
  wipAging61To90?: number | null;
  wipAging91To120?: number | null;
  wipAging121Plus?: number | null;
}

/** Undefined when the API row carries no band split, so the agingDays fallback still applies. */
export function wipAgingBandsFromApi(row: WipAgingBandApiFields): WipAgingBands | undefined {
  const values = [
    row.wipAging0To30, row.wipAging31To60, row.wipAging61To90,
    row.wipAging91To120, row.wipAging121Plus,
  ];
  if (values.every(value => value == null)) return undefined;

  return {
    '0-30': row.wipAging0To30 ?? 0,
    '31-60': row.wipAging31To60 ?? 0,
    '61-90': row.wipAging61To90 ?? 0,
    '91-120': row.wipAging91To120 ?? 0,
    '121+': row.wipAging121Plus ?? 0,
  };
}

export function wipAmountInBand(report: WIPReport, band: WipAgeBand): number {
  return wipAgingBandsFor(report)[band];
}

export function sumWipAgingBands(reports: WIPReport[]): WipAgingBands {
  return reports.reduce<WipAgingBands>((totals, report) => {
    const bands = wipAgingBandsFor(report);
    WIP_AGE_BANDS.forEach(band => { totals[band] += bands[band]; });
    return totals;
  }, emptyWipAgingBands());
}
